(() => {
  if (customElements.get('pm-product')) return;
  customElements.define('pm-product', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.options = { signal: this.controller.signal };
      this.form = this.querySelector('[data-product-form]');
      this.variants = JSON.parse(this.querySelector('[data-product-variants]').textContent);
      this.formatter = new Intl.NumberFormat(this.dataset.currency === 'CLP' ? 'es-CL' : (this.dataset.locale || 'es-CL'), { style: 'currency', currency: this.dataset.currency || 'CLP', maximumFractionDigits: this.dataset.currency === 'CLP' ? 0 : 2 });
      this.form.addEventListener('change', () => this.update(), this.options);
      this.form.addEventListener('input', (event) => { if (event.target.name === 'quantity') this.update(); }, this.options);
      this.form.addEventListener('submit', (event) => this.submit(event), this.options);
      this.setupGallery();
      this.update();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      cancelAnimationFrame(this.scrollFrame);
    }

    money(value) { return this.formatter.format(value / 100); }

    price(variant, quantity) {
      // CLP Shopify amounts are in hundredths, including zero-decimal currencies.
      // Display only the existing automatic discounts after merchant verification.
      const perUnit = this.dataset.volumePrices === 'true' ? (quantity >= 3 ? 1000000 : quantity >= 2 ? 500000 : 0) : 0;
      const savings = Math.min(variant.price, perUnit) * quantity;
      return { total: variant.price * quantity - savings, savings };
    }

    update() {
      const variantId = this.form.querySelector('[data-variant-input]').value;
      const variant = this.variants.find((item) => String(item.id) === variantId);
      if (!variant) return;
      const changed = this.variantId !== variantId;
      this.variantId = variantId;
      const rule = variant.quantity_rule || { min: 1, increment: 1 };
      const radios = [...this.form.querySelectorAll('input[type="radio"][name="quantity"]')];
      radios.forEach((input) => {
        const quantity = Number(input.value);
        input.disabled = !variant.available || quantity < rule.min || (rule.max != null && quantity > rule.max) || (quantity - rule.min) % rule.increment !== 0;
        const prices = this.price(variant, quantity);
        this.querySelector(`[data-pack-price="${quantity}"]`).textContent = this.money(prices.total);
        this.querySelector(`[data-pack-saving="${quantity}"]`).textContent = prices.savings ? `Ahorras ${this.money(prices.savings)}` : `${quantity} ${quantity === 1 ? 'unidad' : 'unidades'}`;
      });
      if (radios.length && !radios.some((input) => input.checked && !input.disabled)) {
        const available = radios.find((input) => !input.disabled);
        if (available) available.checked = true;
      }
      const numberInput = this.form.querySelector('input[type="number"][name="quantity"]');
      if (numberInput) {
        numberInput.min = rule.min;
        numberInput.step = rule.increment;
        if (rule.max != null) numberInput.max = rule.max; else numberInput.removeAttribute('max');
        if (changed) numberInput.value = rule.min;
      }
      const quantityInput = radios.length ? radios.find((input) => input.checked) : numberInput;
      const quantity = Number(quantityInput?.value) || rule.min;
      const prices = this.price(variant, quantity);
      this.querySelector('[data-total-price]').textContent = this.money(prices.total);
      const comparison = prices.savings ? variant.price * quantity : (variant.compare_at_price || 0) * quantity;
      const compareNode = this.querySelector('[data-compare-price]');
      compareNode.textContent = this.money(comparison);
      compareNode.hidden = comparison <= prices.total;
      this.querySelector('[data-price-caption]').textContent = radios.length ? `${quantity} ${quantity === 1 ? 'pack' : 'packs'} · ${quantity * 30} días de rutina` : `${quantity} ${quantity === 1 ? 'unidad' : 'unidades'}`;
      this.button = this.querySelector('[data-product-submit]');
      this.purchasable = variant.available && (!radios.length || radios.some((input) => !input.disabled));
      this.button.disabled = !this.purchasable || this.busy;
      const addLabel = this.button.dataset.addLabel || 'Agregar al carrito';
      this.button.firstChild.textContent = this.busy ? 'Agregando…' : this.purchasable ? `${addLabel} ` : variant.available ? 'Cantidad no disponible ' : 'Agotado ';
      if (changed && variant.featured_media) this.showMedia(variant.featured_media.id);
      if (changed) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      }
    }

    async submit(event) {
      event.preventDefault();
      if (this.busy || !this.purchasable || !this.form.reportValidity()) return;
      this.busy = true;
      const errorNode = this.querySelector('[data-product-error]');
      errorNode.hidden = true;
      this.update();
      this.form.setAttribute('aria-busy', 'true');
      try {
        const payload = new FormData(this.form);
        const response = await fetch(this.dataset.addUrl, { method: 'POST', headers: { Accept: 'application/json' }, body: payload, signal: this.controller.signal });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(typeof result.description === 'string' ? result.description : 'No pudimos agregar esta cantidad. Revisa la disponibilidad e inténtalo nuevamente.');
        // Shopify's cart is authoritative for automatic discounts, stock and totals.
        window.location.assign(this.dataset.cartUrl);
      } catch (error) {
        if (error.name === 'AbortError') return;
        errorNode.textContent = error.message || 'No pudimos conectar. Revisa tu carrito antes de volver a agregar el producto.';
        errorNode.hidden = false;
        errorNode.focus();
        this.busy = false;
        this.update();
      } finally {
        this.form.removeAttribute('aria-busy');
      }
    }

    setupGallery() {
      this.track = this.querySelector('[data-gallery-track]');
      this.slides = [...this.track.querySelectorAll('[data-media-id]')];
      this.thumbnails = [...this.querySelectorAll('[data-gallery-thumbnail]')];
      this.index = 0;
      const controls = this.querySelector('[data-gallery-controls]');
      if (!controls) return;
      controls.hidden = false;
      this.querySelector('[data-gallery-previous]').addEventListener('click', () => this.goTo(this.index - 1), this.options);
      this.querySelector('[data-gallery-next]').addEventListener('click', () => this.goTo(this.index + 1), this.options);
      this.thumbnails.forEach((link, index) => link.addEventListener('click', (event) => { event.preventDefault(); this.goTo(index); }, this.options));
      this.track.addEventListener('keydown', (event) => {
        if (event.target !== this.track || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault(); this.goTo(this.index + (event.key === 'ArrowRight' ? 1 : -1));
      }, this.options);
      this.track.addEventListener('scroll', () => {
        cancelAnimationFrame(this.scrollFrame);
        this.scrollFrame = requestAnimationFrame(() => {
          this.index = Math.max(0, Math.min(this.slides.length - 1, Math.round(this.track.scrollLeft / this.track.clientWidth)));
          this.querySelector('[data-gallery-count]').textContent = `${this.index + 1} / ${this.slides.length}`;
          this.thumbnails.forEach((link, index) => { if (index === this.index) link.setAttribute('aria-current', 'true'); else link.removeAttribute('aria-current'); });
          this.slides.forEach((slide, index) => { if (index !== this.index) slide.querySelectorAll('video').forEach((video) => video.pause()); });
        });
      }, this.options);
    }

    goTo(index) {
      if (!this.slides.length) return;
      this.index = (index + this.slides.length) % this.slides.length;
      this.track.scrollTo({ left: this.track.clientWidth * this.index, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    }

    showMedia(id) {
      const index = this.slides.findIndex((slide) => slide.dataset.mediaId === String(id));
      if (index >= 0) this.goTo(index);
    }
  });
})();
